/**
 * NetForum Login Management
 * Handles member authentication through NetForum redirect
 */

/* NetForum auth (functional, class-free) */
( () => {
	if ( typeof window === 'undefined' || typeof document === 'undefined' )
		return;

	// ---- config/selectors ----
	const SEL = {
		loginTriggers: '[data-login-trigger]',
		paywallOrHeaderTrigger:
			'[data-login-trigger="paywall"], [data-login-trigger="header"]',
		loginText: '[data-login-state-text]',
	};
	const AJAX_URL = () =>
		window.engincPaywall?.ajaxUrl || '/wp/wp-admin/admin-ajax.php';
	const CALLBACK_PATH = () =>
		window.engincPaywall?.callbackPath ?? '/netforum-callback-page/';

	// dedupe guards
	let redirectInFlight = false;
	let validateInFlight = false;

	// ---- boot ----
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}

	function init() {
		bindEvents();
		updateLoginState();
		handleCallbackIfPresent();
		checkForLoginSuccess();
	}

	// ---- events ----
	function bindEvents() {
		document.addEventListener(
			'click',
			( e ) => {
				const trigger = e.target.closest( SEL.paywallOrHeaderTrigger );
				if ( ! trigger ) return;

				// If user is logged in, let the link work normally (don't preventDefault)
				const status = checkMemberStatus();
				if ( status.isLoggedIn ) return;

				e.preventDefault();
				if ( redirectInFlight ) return;

				redirectInFlight = true;
				trigger.setAttribute( 'aria-busy', 'true' );

				redirectToNetForum().finally( () => {
					trigger.removeAttribute( 'aria-busy' );
				} );
			},
			{ passive: false }
		);
	}

	function buildNetforumUrl() {
		const origin = window.location.origin;
		const configuredPath =
			window.engincPaywall?.callbackPath ?? '/netforum-callback-page/';
		const callbackPath = configuredPath.startsWith( '/' )
			? configuredPath
			: `/${ configuredPath }`;

		// Build plain string with literal {token}
		const callbackUrl = `${ origin }${ callbackPath }?userToken={token}`;

		// Encode once, then restore {token}
		const encodedCallback = encodeURIComponent( callbackUrl ).replace(
			/%7Btoken%7D/gi,
			'{token}'
		);

		return `https://netforum.acec.org/eweb/DynamicPage.aspx?WebCode=LoginRequired&URL_Success=${ encodedCallback }`;
	}

	// ---- redirect flow ----
	async function redirectToNetForum() {
		try {
			// Store return URL in session before redirecting
			const requestData = new URLSearchParams( {
				action: 'enginc_store_return_url',
				nonce: getNonce(),
				return_url: window.location.href,
			} );

			await fetch(
				window.engincPaywall?.ajaxUrl || '/wp/wp-admin/admin-ajax.php',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					credentials: 'same-origin',
					body: requestData,
				}
			);
		} catch ( error ) {
			console.error( 'Failed to store return URL:', error );
			// continue with redirect anyway
		}

		// Direct redirect to NetForum with correctly built URL
		const netforumUrl = buildNetforumUrl();
		window.location.assign( netforumUrl );
	}

	// ---- callback/validation ----

	async function handleCallbackIfPresent() {
		const url = new URL( window.location.href );
		const token = url.searchParams.get( 'userToken' );

		// Update debug info if it exists
		const debugStatus = document.getElementById( 'debug-status' );
		if ( debugStatus ) {
			debugStatus.textContent = 'Status: 🚀 Validation starting...';
		}

		// nothing to do if no token or we're already processing
		if ( ! token || validateInFlight ) {
			if ( debugStatus ) {
				debugStatus.textContent =
					'Status: ❌ No token or already processing';
			}
			return;
		}
		validateInFlight = true;

		try {
			const res = await fetch( AJAX_URL(), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				credentials: 'same-origin',
				body: new URLSearchParams( {
					action: 'enginc_netforum_validate',
					nonce: getNonce(),
					userToken: token,
				} ),
			} );

			const data = await safeJson( res );

			if ( debugStatus ) {
				debugStatus.textContent = `Status: Response ${ res.status } - ${
					data?.success ? '✅ SUCCESS' : '❌ FAILED'
				}`;
			}

			if ( ! res.ok || ! data?.success ) {
				throw new Error(
					data?.message || `Validation failed (${ res.status })`
				);
			}

			// server set enginc_member_auth (httpOnly) → update UI
			updateLoginState( true );

			// remove token from URL so refreshes don't re-validate
			url.searchParams.delete( 'userToken' );
			window.history.replaceState( {}, '', url );

			if ( debugStatus ) {
				debugStatus.textContent = 'Status: 🎉 Redirecting...';
			}

			// if server sent a return target, go there
			if ( data.redirect_to ) {
				window.location.assign( data.redirect_to );
			}
			// otherwise stay on the callback page (no explicit return to appease linter)
		} catch ( err ) {
			console.error( 'NetForum validation error:', err );
			if ( debugStatus ) {
				debugStatus.textContent = `Status: ❌ ERROR - ${ err.message }`;
			}
			dispatchToast( 'Login failed. Please try again.' );
		} finally {
			validateInFlight = false;
		}
	}

	// ---- UI state ----
	function updateLoginState( forceLoggedIn = false ) {
		const status = checkMemberStatus();
		const isLoggedIn = forceLoggedIn
			? status.isLoggedIn || true
			: status.isLoggedIn;

		document.querySelectorAll( SEL.loginTriggers ).forEach( ( link ) => {
			const textEl = link.querySelector( SEL.loginText );
			if ( ! textEl ) return;

			const nextState = isLoggedIn ? 'account' : 'login';
			if ( link.dataset.loginUiState === nextState ) return;
			link.dataset.loginUiState = nextState;

			// remove prior click handler if we stored one
			if ( link.__loginHandlerRef ) {
				link.removeEventListener( 'click', link.__loginHandlerRef );
				link.__loginHandlerRef = null;
			}

			// Also remove any generic click handlers that might interfere
			link.onclick = null;

			if ( ! isLoggedIn ) {
				textEl.textContent = 'Login';
				link.href = '#';
				const handler = ( e ) => {
					e.preventDefault();
					redirectToNetForum();
				};
				link.addEventListener( 'click', handler );
				link.__loginHandlerRef = handler;
			} else {
				// User is logged in - show My Account regardless of member status
				textEl.textContent = 'My Account';
				link.href =
					'https://netforum.acec.org/eweb/StartPage.aspx?Site=ACEC';
				link.target = '_blank';
				link.rel = 'noopener noreferrer';

				// Remove login trigger so global handler ignores this link
				link.removeAttribute( 'data-login-trigger' );
			}
		} );
	}

	// ---- cookies / helpers ----
	function checkMemberStatus() {
		// Check for the enginc_member_data cookie set by NetForum callback
		const m = document.cookie.match(
			/(?:^|;\s*)enginc_member_data=([^;]+)/
		);
		if ( ! m ) return { isLoggedIn: false, isMember: false };

		try {
			const decoded = JSON.parse( decodeURIComponent( m[ 1 ] ) );
			return {
				isLoggedIn: true,
				isMember: decoded.vst_member_flag === '1',
			};
		} catch {
			return { isLoggedIn: false, isMember: false };
		}
	}

	function getNonce() {
		const meta = document.querySelector( 'meta[name="enginc-nonce"]' );
		if ( meta ) return meta.content; // server expects 'enginc' on callback page
		return window.engincPaywall?.nonce || '';
	}

	async function safeJson( res ) {
		try {
			return await res.json();
		} catch {
			return null;
		}
	}

	function dispatchToast( message, variant = 'error' ) {
		window.dispatchEvent(
			new CustomEvent( 'enginc:toast', {
				detail: { message, variant },
			} )
		);
	}

	// Toast listener - creates and shows toast messages
	function showToast( message, variant = 'error' ) {
		// Create toast element
		const toast = document.createElement( 'div' );
		toast.className = `fixed bottom-4 left-1/2 transform -translate-x-1/2 translate-y-full z-50 px-6 py-4 rounded-lg shadow-lg max-w-sm transition-all duration-300`;

		// Set variant styles
		if ( variant === 'success' ) {
			toast.className += ' bg-green-600 text-white';
		} else if ( variant === 'warning' ) {
			toast.className += ' bg-yellow-500 text-white';
		} else {
			toast.className += ' bg-red-600 text-white';
		}

		toast.innerHTML = `
			<div class="flex items-center justify-between">
				<span>${ message }</span>
				<button class="ml-4 text-white hover:text-gray-200 font-bold text-lg" onclick="this.parentElement.parentElement.remove()">&times;</button>
			</div>
		`;

		// Add to page
		document.body.appendChild( toast );

		// Animate in from bottom
		setTimeout( () => {
			toast.classList.remove( 'translate-y-full' );
		}, 100 );

		// Auto remove after 5 seconds
		setTimeout( () => {
			if ( toast.parentElement ) {
				toast.classList.add( 'translate-y-full' );
				setTimeout( () => {
					if ( toast.parentElement ) {
						toast.remove();
					}
				}, 300 );
			}
		}, 20000 );
	}

	// Listen for toast events
	window.addEventListener( 'enginc:toast', function ( event ) {
		const { message, variant } = event.detail;
		showToast( message, variant );
	} );

	function checkForLoginSuccess() {
		// Check for the login success cookie set by the callback page
		const cookies = document.cookie;
		let message = '';
		let variant = 'success';

		if ( cookies.includes( 'enginc_login_success=full_access' ) ) {
			message =
				"You're logged in - enjoy full access to Engineering Inc!";
			variant = 'success';
		} else if ( cookies.includes( 'enginc_login_success=no_vst_access' ) ) {
			message =
				"You're logged in, but you need an ACEC membership for magazine access. Contact us to upgrade!";
			variant = 'warning';
		} else if ( cookies.includes( 'enginc_login_success=basic' ) ) {
			message = "You're now logged in - enjoy Engineering Inc!";
			variant = 'success';
		}

		if ( message ) {
			// Clear the cookie
			document.cookie =
				'enginc_login_success=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

			// Update login state since user just logged in
			updateLoginState( true );

			// Small delay to ensure page is fully loaded
			setTimeout( () => {
				dispatchToast( message, variant );
			}, 500 );
		}
	}
} )();
